import { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { EChartsOption, EChartsType, SeriesOption } from 'echarts'
import { sortBy } from 'lodash-es'

import { useAppSelector } from 'app/hooks'
import Echart from 'common/components/Echart'
import { getBarColorScheme } from 'common/utils/chart/bar'
import {
  formatLabelOnNewLine,
  formatWithCommasOrExponential,
  getChartLabelBasedOnRiskUnit,
  makeRUoptions
} from 'common/utils/chart/chartUtils'
import { QRABarColorScheme } from 'common/utils/colorUtils'

import GraphContainerCard from 'features/RiskManagement/Components/Graphs/Components/GraphContainerCard'
import ScrollStepper from 'features/RiskManagement/Components/Graphs/Components/ScrollStepper'
import { selectRiskUnit } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import tailwind from '_/tailwind.config'

/**
 * WARN: This chart uses logarithmic display workaround hack which
 *       makes data retrieved by chart API off by several magnitude
 *       which will have to be post processed in order to be corrected.
 */

/**
 * Magic number to work around log chart display limitation where bar draws
 * from 1 as 0 does not exist on log scale.. this hack will make the chart
 * think we are displaying a larger exponential but we will reverse the
 * the representation in label..
 *
 * WARN: This may break other chart features (such as csv export) which we
 *       may have to reimplement if we are to use them.
 */
const LOG_SCALE_START_POSITION_HACK = 1e10

const formatterOptions = makeRUoptions('ru')

const yAxisValueFormatter = (val: number): string => {
  if (val === LOG_SCALE_START_POSITION_HACK) {
    return '0'
  }

  return formatWithCommasOrExponential(
    val / LOG_SCALE_START_POSITION_HACK,
    formatterOptions
  )
}

interface IRiskByLineChart {
  showLoading: boolean
  categoryIds: number[]
  categories: string[]
  catInteractNavBasePath?: string
  s1Label?: string
  s2Label?: string
  s1Values: number[]
  s2Values?: number[]
  colorScheme?: QRABarColorScheme
  isWideDesktop: boolean
}

const RiskByLineChart = ({
  showLoading,
  categoryIds,
  categories,
  catInteractNavBasePath,
  s1Label,
  s2Label,
  s1Values,
  s2Values,
  colorScheme,
  isWideDesktop
}: IRiskByLineChart) => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const riskUnit = useAppSelector(selectRiskUnit)

  const axisLabelWidth = useMemo(() => {
    return isWideDesktop ? 80 : 56
  }, [isWideDesktop])

  const [echartsInstance, setEchartsInstance] = useState<EChartsType | null>(
    null
  )

  const options = useMemo(() => {
    const formatterOptions = makeRUoptions('ru')

    // sort values by descending and display top 20 (with existing adjustment)
    const _normalizeValues = (values: number[] | undefined) =>
      sortBy(values, [(a, b) => b - a])
        .slice(0, 20)
        .map((value) => value * LOG_SCALE_START_POSITION_HACK)

    const _s1Normalized: number[] = _normalizeValues(s1Values)
    const _s2Normalized: number[] = _normalizeValues(s2Values)

    // merge series data
    const series: SeriesOption[] = [_s1Normalized, _s2Normalized]
      .filter((valuesArray) => valuesArray.length)
      .map((values, index) => {
        const name = !index ? s1Label : s2Label

        return {
          name,
          type: 'bar',
          data: values,
          color: getBarColorScheme(colorScheme)[index]
        }
      })

    const _options: EChartsOption = {
      tooltip: {
        extraCssText: 'box-shadow: 10 10 3px rgba(250, 10, 20, 0.3);',
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        valueFormatter: (val) =>
          formatWithCommasOrExponential(
            (val as number) / LOG_SCALE_START_POSITION_HACK,
            formatterOptions
          )
      },
      grid: {
        top: s2Values ? 36 : 12,
        left: 76,
        right: 24,
        bottom: 60
      },
      legend: {
        show: series.length > 1,
        left: 0,
        itemHeight: 12,
        itemWidth: 12
      },
      xAxis: {
        data: categories,
        name: 'Continuous Route',
        nameLocation: 'middle',
        nameGap: 48,
        nameTextStyle: {
          color: tailwind.theme.colors.black,
          fontWeight: 'bold',
          align: 'center'
        },
        axisLabel: {
          formatter: (val: number) => formatLabelOnNewLine(val),
          lineHeight: 16,
          interval: 0,
          width: axisLabelWidth,
          overflow: 'truncate'
        },
        axisLine: {
          // un-bold the xAxis
          show: false
        },
        axisTick: {
          show: false
        },
        // Enable x-axis label click event
        triggerEvent: true
      },
      yAxis: {
        type: 'log',
        min: LOG_SCALE_START_POSITION_HACK, // ensure y-axis starts at 0
        name: getChartLabelBasedOnRiskUnit(
          riskUnit,
          'Risk (RU/yr)',
          // Includes line break because label is too long and goes off screen
          'Risk (RU/mi/yr)'
        ),
        nameLocation: 'middle',
        // REVIEW: echarts#9265
        nameGap: 62,
        nameTextStyle: {
          color: tailwind.theme.colors.black,
          fontWeight: 'bold'
        },
        axisLabel: {
          formatter: (val: number) => yAxisValueFormatter(val)
        }
      },
      series,
      dataZoom: [
        {
          id: 'xAxis-innerScroll',
          type: 'inside',
          xAxisIndex: [0],
          // prevent axis scale update
          filterMode: 'none',
          // items to show (0 indexed)
          maxValueSpan: 9,
          minValueSpan: 9,
          zoomLock: false,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: true
        },
        {
          id: 'xAxis-outerScroll',
          type: 'slider',
          xAxisIndex: [0],
          // hide start/end label on range select
          showDetail: false,
          showDataShadow: false,
          // fully hidden
          bottom: 0,
          height: 0,
          // background color of selected range area
          // fillerColor: tailwind.theme.colors.coolGray[300],
          // prevent range change
          zoomLock: true,
          // hide range change handle
          handleSize: 0,
          brushSelect: false
        }
      ]
    }

    return _options
  }, [
    categories,
    colorScheme,
    riskUnit,
    s1Label,
    s1Values,
    s2Label,
    s2Values,
    axisLabelWidth
  ])

  // REVIEW: This will always update due to array reference
  //         should implement some sort of deep array comparison
  const chartClickHandler = useCallback(
    (evt: { name: string; value: string }): void => {
      if (catInteractNavBasePath == null) {
        return
      }

      const selectedCategory: string = evt?.name ?? evt?.value
      const idx = categories.indexOf(selectedCategory)

      if (idx >= 0) {
        const categoryId = categoryIds[idx]

        navigate(`${catInteractNavBasePath}/${categoryId}${search}`)
      } else {
        console.warn(
          `No navigation route for selected category: ${selectedCategory}`
        )
      }
    },
    [catInteractNavBasePath, categories, categoryIds, navigate, search]
  )

  // this is the callback ref you want to trigger the render for ScrollStepper
  // ref comes in too early and onChartReady spits out new charts every render
  const finishedHandler = useCallback(
    (_event: object, instance: EChartsType) => {
      if (instance) {
        setEchartsInstance(instance)
      } else {
        setEchartsInstance(null)
      }
    },
    []
  )

  // NOTE: handlers need to be wrapped in `useCallback`
  // However, update in handler will still cause chart to refresh fully
  const handleChartEvents = {
    click: chartClickHandler,
    finished: finishedHandler
  }

  return (
    <GraphContainerCard
      title='Risk by Route'
      optionsComponent={
        <ScrollStepper
          showLoading={showLoading}
          echartsInstance={echartsInstance}
        />
      }
    >
      <Echart
        notMerge
        option={options}
        showLoading={showLoading}
        onEvents={handleChartEvents}
      />
    </GraphContainerCard>
  )
}

export default RiskByLineChart
